/**
 * Whole-gallery quick-look modal (tiles plan U3). Thumbnails only — no
 * carousel inside. Content is server-rendered HTML from the same fragment
 * endpoint the tile carousels use, so both browse modes always show the
 * same images and no markup is built in JavaScript.
 */

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

let lastTrigger = null;
let keydownBound = false;
// One shared modal body means the last response to arrive wins. Every open
// takes a token; a fetch that resolves after a newer open is discarded
// rather than filling the modal with a different location's gallery.
let openToken = 0;

function modal() {
    const el = document.getElementById("il-tile-gallery-modal");

    // The shell is authored inside the page content, which sits in a
    // `z-index: 0` stacking context — so a fixed overlay declaring z-index
    // 1050 in there still loses to the header's search button at z-index 2,
    // which lands exactly on the close control. Reparenting to <body> puts
    // it in the root stacking context, where 1050 means what it says. Done
    // here rather than in the template so it holds wherever the shell is
    // rendered.
    if (el && el.parentElement !== document.body) {
        document.body.appendChild(el);
    }

    return el;
}

function close() {
    const el = modal();
    if (!el || el.hidden) return;

    el.hidden = true;
    document.body.classList.remove("il-modal-open");

    // Return focus where the user left it — a keyboard user who closes the
    // modal must not lose their place in the grid.
    if (lastTrigger) {
        lastTrigger.focus();
        lastTrigger = null;
    }
}

/**
 * Keep Tab inside the dialog while it is open.
 *
 * @param {KeyboardEvent} event
 * @returns {void}
 */
function trapFocus(event) {
    const el = modal();
    if (!el || el.hidden) return;

    if (event.key === "Escape") {
        close();
        return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(el.querySelectorAll(FOCUSABLE)).filter((node) => node.offsetParent !== null);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
}

/**
 * @param {Element} trigger the magnifier button that was clicked
 * @returns {void}
 */
export default function openGalleryModal(trigger) {
    const el = modal();
    if (!el) return;

    const token = ++openToken;
    lastTrigger = trigger;

    const title = el.querySelector(".il-tile-gallery-title");
    const body = el.querySelector(".il-tile-gallery-body");
    if (title) title.textContent = trigger.dataset.locationTitle || "Gallery";

    // Open immediately with a loading state — an empty box while the fetch
    // resolves reads as broken.
    if (body) {
        body.innerHTML = '<p class="text-center py-5 mb-0 il-tile-gallery-status">Loading images…</p>';
    }
    el.hidden = false;
    document.body.classList.add("il-modal-open");

    const closeBtn = el.querySelector(".il-tile-gallery-close");
    if (closeBtn) closeBtn.focus();

    if (!keydownBound) {
        document.addEventListener("keydown", trapFocus);
        el.addEventListener("click", (event) => {
            if (event.target === el || event.target.closest(".il-tile-gallery-close")) {
                close();
            }
        });
        keydownBound = true;
    }

    // Event context comes from the tile (i.e. the page), so the modal shows
    // the same images the tile's carousel does.
    const query = "?view=thumbs" + (trigger.dataset.event === "1" ? "&event=1" : "");

    fetch(`/location-library/tile/${encodeURIComponent(trigger.dataset.locationSlug)}/slides${query}`, {
        headers: { Accept: "text/html" },
    })
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
            if (!body || token !== openToken) return; // a newer open won
            body.innerHTML = html.trim()
                ? html
                : '<p class="text-center py-5 mb-0 il-tile-gallery-status">No images yet.</p>';

            // Share buttons are CSS-hidden until marked ready — a hook the
            // lazy-image loader normally fires on image load. These carry
            // real media ids the moment they exist, and nothing lazy-loads
            // them, so mark them here or they would never appear.
            body.querySelectorAll(".il-share-image-btn").forEach((btn) => {
                btn.classList.add("il-share-ready");
            });
        })
        .catch(() => {
            if (body && token === openToken) {
                body.innerHTML = '<p class="text-center py-5 mb-0 il-tile-gallery-status">Couldn’t load images.</p>';
            }
        });
}
